import React from 'react';

import { PriceSimulatorTruckName } from '@models/next/PriceSimulator';
import type { PriceSimulator2Form } from '@models/PriceSimulator';
import { createStrings } from '@utils/local';

export const priceSimulatorStrings = createStrings({
  ja: {
    priceSimulator: '料金シミュレーター',
    priceSimulatorResult: '料金シミュレーター結果',
    lwhcalc: '荷物明細計算',
    wvcalc: '総重量体積計算',
    on_sea_lead_time: '海上所要日数',
    on_truck_lead_time: '配達必要予測日数',
    total_lead_time: '配達所要日数（最短）',
    base_kg: '基準計算重量',
    non_target_tax_price: '運送料金非課税額',
    target_tax_price: '運送料金課税額',
    shipping_tax: '消費税',
    shipping_unit_price: '輸送単価',
    total_price: '合計金額',
    chinaCharges: '中国国内料金（CHINA LOCAL CHARGE）',
    unknownError:
      '申し訳ございませんが、予期せぬエラーが発生しました。もう一度お試し、またはお問い合わせにお声かけください。',
    noRoutes:
      '申し訳ございませんが、シミュレートはできませんでした。荷物の内容・荷受倉庫・集荷先・納品先・納品オプションなどを変えたり、お問い合わせにお声かけたりしてください。',
    tooManyCargos:
      '荷物の重量上限（9850 KGS）または容積上限（35 CBM）を超えています。荷物の内容を変えたり、お問い合わせにお声かけたりしてください。',
    invalidDeliveryOptions:
      'ご希望の納品オプションはご利用になれませんので、「指定なし」または他の選択肢をお選びください。',
    invalidCargosPortName:
      'ご希望の荷受倉庫はご利用になれませんので、ほかの荷受倉庫をお選びください。',
    expectedCostHint:
      '*こちらの見積金額は、お客様にご入力いただいた貨物情報をもとに算出した概算見積金額です。物量の増減や配送方法のオプション選択などによって金額が変動する場合がございます。',
    includedCostTitle: '■見積金額に含まれる料金',
    excludedCostTitle: '■見積金額に含まれない料金',
    tax: '日本の輸入関税、輸入消費税',
    clearance: '税関検査料金 ※発生時のみ',
    insurance: '外航貨物海上保険保険料 ※必要な場合のみ',
    reference: () => (
      <>
        ■備考
        <ul>
          <li>
            シミュレーターの料金には、離島など一部地域への追加送料は含まれておりません。別途計算が必要になる場合がございます。
          </li>
          <li>輸送中の道路状況、天候などによって、結果通りの輸送日数ではない場合もございます。</li>
          <li>
            極めて限定的ですが、D2Dがサービスを提供していない地域があります。詳細は当社へお問い合わせください。
          </li>
          <li>
            1個の貨物実重量は1000KG以上、もしくは1個の貨物、1辺サイズが2M以上がある場合、料金が変動する可能性があります。
          </li>
          <li>アマゾン/楽天への納品は事前の連絡をお願いします。</li>
          <li>
            料金設定や計算方法に変更が生じる場合がございますので、ご依頼までに期間が開く場合には再度料金シミュレーターでご確認ください。
          </li>
        </ul>
      </>
    ),
  },
});

export interface Step {
  name: string;
  label: string;
}

export const steps: Step[] = [
  {
    name: 'LWHCalc',
    label: priceSimulatorStrings.lwhcalc,
  },
  {
    name: 'WVCalc',
    label: priceSimulatorStrings.wvcalc,
  },
];

export const cargosLimitByTruckOptions: Record<
  PriceSimulatorTruckName,
  { kg: number; cbm: number }
> = {
  any: {
    kg: 9850,
    cbm: 35,
  },
  mix: {
    kg: 1000,
    cbm: 3,
  },
  '4T': {
    kg: 2750,
    cbm: 18,
  },
  '10T': {
    kg: 9850,
    cbm: 35,
  },
  '4Tx2': {
    kg: 5500,
    cbm: 35,
  },
};

export const priceSimulator2FormInitialValuesHasPickups: PriceSimulator2Form = {
  has_pickups: 'true' as any,
  cargos_port_name: '',
  receiver: {
    city_cd: '',
    pref: '',
    zip_code: '540-0002',
  },
  pickup_addresses: [
    {
      id: 1,
      zip_code: '518000',
      city_name: 'Guandong, Shenzhen',
    },
    {
      id: 2,
      zip_code: '519000',
      city_name: 'Guandong, Zhuhai',
    },
  ],
  cargos2: [
    {
      id: 40026,
      tag: 'WV',
      name: 'TSUKIKAGE',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
    {
      id: 3271,
      tag: 'WV',
      name: 'HANATSUKIYO',
      num: 1,
      total_volume: 3,
      total_weight: 2,
      pickup_address_index: 2,
    },
    {
      id: 62963,
      tag: 'WV',
      name: 'TABI',
      num: 1,
      total_volume: 1,
      total_weight: 10,
      pickup_address_index: 2,
    },
    {
      id: 31686,
      tag: 'WV',
      name: 'SOUGETSU',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
  ],
  truck_type: 'any',
};
